import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Photo as PhotoIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchManuals, deleteManual } from '../../store/slices/manualsSlice';
import { fetchMachines } from '../../store/slices/machinesSlice';
import { Manual } from '../../types/manual';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return 'N/A';
  }
  try {
    const date = parseISO(dateString);
    if (!isValid(date) || date.getFullYear() < 1900) {
      return 'N/A';
    }
    return format(date, 'dd.MM.yyyy', { locale: de });
  } catch {
    return 'N/A';
  }
};

export const ManualsOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { manuals, isLoading } = useAppSelector((state) => state.manuals);
  const { machines } = useAppSelector((state) => state.machines);
  const { user } = useAppSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [machineFilter, setMachineFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedManual, setSelectedManual] = useState<Manual | null>(null);

  const canEdit = user?.role === 'admin';

  useEffect(() => {
    // Fetch all machines for filter dropdown
    dispatch(fetchMachines({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Fetch manuals for all machines
  useEffect(() => {
    // Get all unique machine IDs and fetch manuals for each
    if (machines.length > 0) {
      machines.forEach(machine => {
        dispatch(fetchManuals(machine.id));
      });
    }
  }, [dispatch, machines]);

  const handleRefresh = () => {
    machines.forEach(machine => {
      dispatch(fetchManuals(machine.id));
    });
  };

  const handleViewManual = (manual: Manual) => {
    navigate(`/manuals/${manual.id}`);
  };

  const handleDeleteRequest = (manual: Manual) => {
    setSelectedManual(manual);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedManual) {
      await dispatch(deleteManual(selectedManual.id));
      setDeleteDialogOpen(false);
      setSelectedManual(null);
      handleRefresh();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedManual(null);
  };

  // Get machine names for a manual (supports many-to-many relationship)
  const getMachineNames = (manual: Manual): string => {
    if (!manual.machines || manual.machines.length === 0) {
      return 'Unknown Machine';
    }
    return manual.machines.map(m => m.name).join(', ');
  };

  // Filter manuals
  const filteredManuals = manuals.filter(manual => {
    if (!manual) return false;

    const matchesSearch = !searchQuery ||
      manual.title.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = typeFilter === 'all' || manual.type === typeFilter;

    const matchesMachine = machineFilter === 'all' ||
      manual.machines?.some(m => m.id === machineFilter);

    return matchesSearch && matchesType && matchesMachine;
  });

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: 'Typ',
      width: 80,
      renderCell: (params) => (
        params.value === 'pdf' ?
          <PdfIcon color="error" /> :
          <PhotoIcon color="primary" />
      ),
    },
    {
      field: 'title',
      headerName: 'Titel',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'machines',
      headerName: 'Maschine',
      width: 200,
      valueGetter: (value: any, row: any) => getMachineNames(row),
    },
    {
      field: 'page_count',
      headerName: 'Seiten',
      width: 100,
      valueFormatter: (value: any) => value || 'N/A',
    },
    {
      field: 'created_at',
      headerName: 'Erstellt',
      width: 120,
      valueFormatter: (value: any) => formatDate(value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 120,
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            icon={<ViewIcon />}
            label="Anzeigen"
            onClick={() => handleViewManual(params.row as Manual)}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => handleDeleteRequest(params.row as Manual)}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Anleitungen Übersicht
        </Typography>
        <Box display="flex" gap={1}>
          <IconButton onClick={handleRefresh} title="Aktualisieren">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Anleitungen durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={typeFilter}
              label="Typ"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Typen</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="photo">Foto</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Maschine</InputLabel>
            <Select
              value={machineFilter}
              label="Maschine"
              onChange={(e) => setMachineFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Maschinen</MenuItem>
              {machines.map((machine) => (
                <MenuItem key={machine.id} value={machine.id}>
                  {machine.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredManuals.length} von {manuals.length} Anleitung(en)
        </Typography>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredManuals}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Anleitung löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie die Anleitung "{selectedManual?.title}" wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
